import React, { useState, useEffect } from 'react';
import { useSecurity } from './contexts/SecurityContext'; 
import { useAuth } from './contexts/AuthContext';       
import { apiService } from './services/api'; 
import './style/PageStyle/Settings.css'; 
import './style/PageStyle/Documents.css'; 

const Settings = () => {
  const { user } = useAuth(); 
  const { openKeySetup } = useSecurity();

  // --- Email Configuration State ---
  const [emailConfig, setEmailConfig] = useState({
    service: 'gmail', // 'gmail', 'office365', or 'amazon_ses'
    user: '',
    pass: '',
    host: '', // Added for Amazon SES
    fromEmail: '' // <--- ADDED: Sender Email Address
  });
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // --- DocuSign Configuration State ---
  const [docusignConfig, setDocusignConfig] = useState({
    clientId: '',       
    userId: '',         
    accountId: '',
    basePath: '', 
    privateKey: ''      
  });
  const [loadingDocusign, setLoadingDocusign] = useState(false);

  // Fetch existing settings on load (ONLY IF ADMIN)
  useEffect(() => {
    if (user?.role !== 'admin') return;

    const fetchSettings = async () => {
      try {
        // 1. Fetch Email Settings
        const emailData = await apiService.getEmailSettings();
        if(emailData) {
          setEmailConfig({ 
            service: emailData.service || 'gmail', 
            user: emailData.user || '', 
            pass: emailData.pass || '',
            host: emailData.host || '',
            fromEmail: emailData.fromEmail || '' // <--- Load fromEmail
          });
        }

        // 2. Fetch DocuSign Settings
        const dsData = await apiService.getDocuSignSettings(); 
        if(dsData) {
          setDocusignConfig({
            clientId: dsData.clientId || '',
            userId: dsData.userId || '',
            accountId: dsData.accountId || '',
            basePath: dsData.basePath || 'https://demo.docusign.net/restapi', 
            privateKey: dsData.privateKey || ''
          });
        }

      } catch (error) {
        console.error("Error fetching settings", error);
      }
    };
    fetchSettings();
  }, [user]); 

  // --- Handlers for Email ---
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEmail = async () => {
    setLoadingEmail(true);
    try {
      await apiService.updateEmailSettings(emailConfig);
      alert("Email settings saved successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to save email settings.");
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const testPayload = {
        ...emailConfig,
        targetEmail: user?.email
      };
      
      await apiService.testEmailConnection(testPayload);
      alert(`Test email sent to ${user?.email || 'your email'}! Check your inbox.`);
    } catch (error) {
      console.error(error);
      alert("Connection failed. Check credentials.");
    } finally {
      setTestingEmail(false);
    }
  };


  // --- Handlers for DocuSign ---
  const handleDocusignChange = (e) => {
    const { name, value } = e.target;
    setDocusignConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDocusign = async () => {
    setLoadingDocusign(true);
    try {
      const payload = {
        clientId: docusignConfig.clientId,
        userId: docusignConfig.userId,
        accountId: docusignConfig.accountId,
        basePath: docusignConfig.basePath, 
        privateKey: docusignConfig.privateKey
      };

      await apiService.saveDocuSignSettings(payload);
      alert("DocuSign settings saved successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to save DocuSign settings: " + error.message);
    } finally {
      setLoadingDocusign(false);
    }
  };

  // Helper to determine label text
  const getUsernameLabel = () => {
    switch (emailConfig.service) {
      case 'amazon_ses': return 'SMTP Username (Access Key)';
      case 'office365': return 'Office 365 Email Address';
      default: return 'Gmail Address';
    }
  };

  const getPasswordLabel = () => {
    switch (emailConfig.service) {
      case 'amazon_ses': return 'SMTP Password (Secret Key)';
      case 'office365': return 'Microsoft App Password';
      default: return 'Google App Password';
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage your account and security</p>
        </div>
      </div>

      <div className="settings-container">
        
        {/* --- User Profile Section --- */}
        <div className="settings-card">
          <h2 className="settings-card-title">User Profile</h2>
          
          <div className="settings-row">
            <strong>Username:</strong> <span>{user?.username || 'N/A'}</span>
          </div>
          
          <div className="settings-row">
            <strong>Email:</strong> <span>{user?.email || 'N/A'}</span>
          </div>

          <div className="settings-row">
            <strong>Phone:</strong> <span>{user?.phone || user?.phoneNumber || 'N/A'}</span>
          </div>

          <div className="settings-row">
            <strong>Role:</strong> 
            <span className="text-capitalize">{user?.role || 'User'}</span>
          </div>

          <div className="settings-row">
            <strong>Status:</strong> 
            <span className={`status-badge ${user?.status === 'active' ? 'status-active' : ''}`}>
              {user?.status || 'Active'}
            </span>
          </div>
        </div>

        {/* --- Security Section --- */}
        <div className="settings-card">
          <h2 className="settings-card-title">Security & Authentication</h2>
          <p className="settings-description">
            To protect sensitive documents, you must register a hardware security key 
            (TouchID, FaceID, or YubiKey). This is required for downloading and deleting files.
          </p>
          
          <div className="settings-action-area">
            <button className="btn-primary" onClick={openKeySetup}>
              + Register New Security Key
            </button>
            <p className="text-muted">
              Note: Clicking this will prompt your browser to save a passkey.
            </p>
          </div>
        </div>

        {/* --- ADMIN ONLY SECTIONS --- */}
        {user?.role === 'admin' && (
          <>
            {/* --- Email Configuration Section --- */}
            <div className="settings-card">
              <h2 className="settings-card-title">Host Email Setting</h2>
              <p className="settings-description">
                Configure the email account used to send 2FA codes and Document Expiry alerts.
              </p>

              <div className="settings-form-group">
                <label className="settings-label">Email Service Provider</label>
                <select 
                  className="settings-input"
                  name="service"
                  value={emailConfig.service}
                  onChange={handleEmailChange}
                >
                  <option value="gmail">Gmail (Google Workspace)</option>
                  <option value="office365">Microsoft Office 365 (Outlook)</option>
                  <option value="amazon_ses">Amazon SES (Simple Email Service)</option>
                </select>
              </div>

              {/* Conditional Input for Amazon SES Host */}
              {emailConfig.service === 'amazon_ses' && (
                <div className="settings-form-group">
                  <label className="settings-label">SMTP Endpoint (Host)</label>
                  <input 
                    className="settings-input"
                    type="text" 
                    name="host" 
                    value={emailConfig.host || ''} 
                    onChange={handleEmailChange}
                    placeholder="email-smtp.us-east-1.amazonaws.com"
                  />
                  <small className="text-muted">
                    Found in AWS Console &gt; Amazon SES &gt; SMTP Settings.
                  </small>
                </div>
              )}

              {/* NEW: Sender Email Field */}
              <div className="settings-form-group">
                <label className="settings-label">Sender Email Address (From)</label>
                <input 
                  className="settings-input"
                  type="email" 
                  name="fromEmail" 
                  value={emailConfig.fromEmail || ''} 
                  onChange={handleEmailChange}
                  placeholder="notifications@yourcompany.com"
                />
                <small className="text-muted">
                  {emailConfig.service === 'amazon_ses' 
                    ? "Must be a Verified Identity in AWS SES." 
                    : "The email address that recipients will see."}
                </small>
              </div>

              <div className="settings-form-group">
                <label className="settings-label">
                  {getUsernameLabel()}
                </label>
                <input 
                  className="settings-input"
                  type="text" 
                  name="user" 
                  value={emailConfig.user || ''} 
                  onChange={handleEmailChange}
                  placeholder={emailConfig.service === 'amazon_ses' ? "AKIA..." : "notifications@company.com"}
                />
              </div>

              <div className="settings-form-group">
                <label className="settings-label">
                  {getPasswordLabel()}
                </label>
                <input 
                  className="settings-input"
                  type="password" 
                  name="pass" 
                  value={emailConfig.pass || ''} 
                  onChange={handleEmailChange}
                  placeholder="xxxx xxxx xxxx xxxx"
                />
                
                {/* Dynamic Help Text based on Service Selection */}
                {emailConfig.service === 'gmail' && (
                  <small className="text-muted" style={{marginTop: '5px', display: 'block'}}>
                    Go to Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords to generate one.
                  </small>
                )}
                {emailConfig.service === 'office365' && (
                  <small className="text-muted" style={{marginTop: '5px', display: 'block'}}>
                    Ensure "Authenticated SMTP" is enabled. If MFA is on, use an App Password.
                  </small>
                )}
                {emailConfig.service === 'amazon_ses' && (
                  <small className="text-muted" style={{marginTop: '5px', display: 'block'}}>
                    Use your <strong>SMTP Credentials</strong> (not your AWS IAM User password). Create these in SES &gt; SMTP Settings &gt; Create My SMTP Credentials.
                  </small>
                )}
              </div>

              <div className="settings-action-area button-row">
                <button 
                  className="action-btn secondary" 
                  onClick={handleTestEmail}
                  disabled={testingEmail || !emailConfig.user || !emailConfig.pass}
                >
                  {testingEmail ? 'Sending...' : 'Test Connection'}
                </button>
                
                <button 
                  className="btn-primary" 
                  onClick={handleSaveEmail}
                  disabled={loadingEmail}
                >
                  {loadingEmail ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>

            {/* --- DocuSign Configuration Section --- */}
            <div className="settings-card">
              <h2 className="settings-card-title">DocuSign API Configuration</h2>
              <p className="settings-description">
                Configure your DocuSign Developer account details here. These are required to send contracts.
                <br />
                <small className="text-muted">
                  Find these in DocuSign Admin &gt; Settings &gt; Apps and Keys.
                </small>
              </p>

              <div className="settings-form-group">
                <label className="settings-label">Integration Key (Client ID)</label>
                <input 
                  className="settings-input"
                  type="text" 
                  name="clientId" 
                  value={docusignConfig.clientId || ''} 
                  onChange={handleDocusignChange}
                  placeholder="e.g., 7c2b1d9a-..."
                />
              </div>

              <div className="settings-form-group">
                <label className="settings-label">User ID (Impersonated User GUID)</label>
                <input 
                  className="settings-input"
                  type="text" 
                  name="userId" 
                  value={docusignConfig.userId || ''} 
                  onChange={handleDocusignChange}
                  placeholder="e.g., 4f1a2b3c-..."
                />
              </div>

              <div className="settings-form-group">
                <label className="settings-label">API Account ID</label>
                <input 
                  className="settings-input"
                  type="text" 
                  name="accountId" 
                  value={docusignConfig.accountId || ''} 
                  onChange={handleDocusignChange}
                  placeholder="e.g., 12345678"
                />
              </div>

              <div className="settings-form-group">
                <label className="settings-label">Base Path (API URL)</label>
                <input 
                  className="settings-input"
                  type="text" 
                  name="basePath" 
                  value={docusignConfig.basePath || ''} 
                  onChange={handleDocusignChange}
                  placeholder="https://demo.docusign.net/restapi"
                />
                <small className="text-muted">
                  Usually <code>https://demo.docusign.net/restapi</code> for dev, or <code>https://na2.docusign.net/restapi</code> for prod.
                </small>
              </div>

              <div className="settings-form-group">
                <label className="settings-label">RSA Private Key</label>
                <textarea 
                  className="settings-input"
                  name="privateKey" 
                  value={docusignConfig.privateKey || ''} 
                  onChange={handleDocusignChange}
                  rows="6"
                  placeholder="-----BEGIN RSA PRIVATE KEY----- ..."
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <small className="text-muted">
                  Copy the entire key including the BEGIN and END lines.
                </small>
              </div>

              <div className="settings-action-area">
                <button 
                  className="btn-primary" 
                  onClick={handleSaveDocusign}
                  disabled={loadingDocusign}
                >
                  {loadingDocusign ? 'Saving...' : 'Save DocuSign Settings'}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Settings;
